import { useContext, useInsertionEffect, useMemo } from 'react';
import { ServerStylesContext } from './serverStyles';
import { compileElementStyles, type CompiledElementStyles, type ElementStyleInput } from './compileElementStyles';
import { mountElementStyles } from './styleSheet';

// What the renderer calls: one hook, one class name back, and the rule behind it looked after for as long
// as the element is on the page.
//
// The rule is written in an insertion effect rather than a layout effect, so it lands before React reads a
// single box and before the browser paints — an element never flashes unstyled on its first frame. The
// class name itself comes out of `useMemo`, which runs on a server too, so a page rendered without a
// browser and the same page hydrated in one agree on what every element is called.

// Compiles an element's styles, only when its styles, the widths it hides at, or the store's tokens change.
export function useElementStyleRules(input: ElementStyleInput): CompiledElementStyles {
  const { theme, styles, hiddenOn } = input;
  return useMemo(() => compileElementStyles({ theme, styles, hiddenOn }), [theme, styles, hiddenOn]);
}

// Answers the class one element wears, keeping its rule in the page's stylesheet for as long as it is drawn.
export function useElementStyles(input: ElementStyleInput): string {
  const compiled = useElementStyleRules(input);
  // On the server, where the effect below never runs, the drawing collects the rule for the page's head instead.
  useContext(ServerStylesContext)?.rules.push(compiled);
  useInsertionEffect(() => mountElementStyles(compiled), [compiled]);
  return compiled.className;
}
